import type { AvailableUnit, UnitDetailsData } from '@/core/api/models/properties';
import { normalizeAmenitiesList } from '@/modules/property-details/services/amenityService';
import type { UnitDetails, UnitDocument } from '../types/property.types';

function parseBedrooms(value: string | undefined): number {
  if (!value) {
    return 0;
  }
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

function mapDocuments(
  _documents: UnitDetailsData['documents'],
): UnitDocument[] {
  // Brochure / master plan cards live on project surfaces (Contentful).
  // Unit detail does not render SF document stubs as empty cards.
  return [];
}

/**
 * Normalise ADHA loan values from profile / catalogue APIs (number or numeric string).
 */
export function parsePositiveLoanAmountAed(value: unknown): number | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : undefined;
  }
  if (typeof value === 'string') {
    const normalized = value.replaceAll(',', '').trim();
    if (!normalized) {
      return undefined;
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  }
  return undefined;
}

/** Normalise AED amounts from API (number or numeric string). Missing → 0. */
export function normalizeAedAmount(value: unknown): number {
  if (value === null || value === undefined) {
    return 0;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Math.max(0, value) : 0;
  }
  if (typeof value === 'string') {
    const normalized = value.replaceAll(',', '').trim();
    if (!normalized) {
      return 0;
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
  }
  return 0;
}

/**
 * Picks the first usable sanctioned loan amount. ADHA sends 0 when the customer
 * has no approved loan, and project details can omit the field entirely, so both
 * cases must fall through to the next source.
 */
export function resolveSanctionedLoanAmount(
  ...amounts: unknown[]
): number | undefined {
  for (const amount of amounts) {
    const parsed = parsePositiveLoanAmountAed(amount);
    if (parsed !== undefined) {
      return parsed;
    }
  }
  return undefined;
}

/**
 * Maps API `UnitDetailsData` → catalogue UI `UnitDetails`.
 * Catalogue fields are expected to be merged from listing before calling this.
 */
export function mapUnitDetailsDataToUi(
  details: UnitDetailsData,
  unitId: string,
): UnitDetails {
  const completion = Number.parseInt(details.completion_percentage, 10);
  const preApprovalAmount = parsePositiveLoanAmountAed(
    details.sanctioned_loan_amount,
  );

  const mapped: UnitDetails = {
    id: details.unit_id || unitId || details.project_id,
    unitNumber: details.unit_number || '',
    projectName: details.project_name,
    handoverDate:
      typeof details.handover_date === 'string'
        ? details.handover_date.trim()
        : '',
    totalPrice: details.total_price ?? 0,
    bedrooms: parseBedrooms(details.number_of_bedrooms),
    bathrooms: details.number_of_bathrooms ?? 0,
    areaSqFt: details.total_area ?? 0,
    amenities: normalizeAmenitiesList(details.amenities),
    documents: mapDocuments(details.documents),
    latitude: details.latitude ?? 0,
    longitude: details.longitude ?? 0,
    projectLocation: details.project_location || '',
    ...(preApprovalAmount !== undefined ? { preApprovalAmount } : {}),
  };

  if (Number.isFinite(completion)) {
    mapped.constructionCompletedPercent = completion;
  }

  return mapped;
}

/**
 * Combine listing row (unit-specific) with project details from ADHA login.
 * No per-unit details API call — amenities / loan / project come from login payload.
 */
export function mergeListingWithProjectDetails(
  listing: AvailableUnit,
  projectDetails: UnitDetailsData | null | undefined,
): UnitDetailsData {
  const base: UnitDetailsData = projectDetails
    ? { ...projectDetails }
    : {
        full_name: '',
        adha_application_number: '',
        sanctioned_loan_amount: 0,
        email: '',
        phone_number: '',
        emirates_id: '',
        created_date: '',
        project_id: '',
        project_name: listing.project_name || '',
        amenities: [],
        completion_percentage: '',
        handover_date: listing.handover_date || '',
        project_location: '',
      };

  const merged: UnitDetailsData = {
    ...base,
    unit_id: listing.unit_id,
    unit_number: listing.unit_number,
    unit_type: listing.unit_type,
    number_of_bedrooms: listing.number_of_bedrooms,
    number_of_bathrooms: listing.number_of_bathrooms,
    total_price: listing.total_price,
    total_area: listing.total_area,
    latitude: listing.latitude,
    longitude: listing.longitude,
    handover_date:
      (typeof listing.handover_date === 'string' && listing.handover_date.trim()
        ? listing.handover_date.trim()
        : undefined) ||
      (typeof base.handover_date === 'string' && base.handover_date.trim()
        ? base.handover_date.trim()
        : '') ||
      '',
    project_name: listing.project_name || base.project_name || '',
  };

  if (listing.images) {
    merged.images = listing.images;
  } else if (base.images) {
    merged.images = base.images;
  }

  return merged;
}
